import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MailRounded from '@mui/icons-material/MailRounded';
import { EmptyState, ListTable, MetricCard, Panel, StatusChip, type ListColumn, type MetricTrend } from '../../../ui-kit';
import {
  fetchStlSpotlight, watchStlLeads, type StlDay, type StlLeadCard, type StlSpotlight,
} from '../../../services/speedToLeadService';
import AgentFlow, { type FlowNode } from '../rocco/AgentFlow';
import { fromNow, useLive } from '../rocco/live';
import { AgencyZoomMark, TwilioMark } from '../missed-call/logos';
import LeadStoryModal from './LeadStory';
import { ElevenLabsMark, HubSpotMark } from './logos';
import { liveChip } from './status';

const whole = (v: number) => v.toLocaleString();

/** "within 30 seconds", "within 2 minutes", "at once". */
function waitWords(seconds: number): string {
  if (seconds <= 0) return 'at once';
  if (seconds < 60) return `within ${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `within ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** What the page draws before the figures arrive: every part present, all empty. */
const EMPTY: StlSpotlight = {
  flow: { agency: '', assistant: '', specialist_phone: null, transfer_line: null,
    call_after_seconds: 30, email_domains: [], chat_minutes: 15 },
  totals: { leads: 0, emailed: 0, called: 0, transferred: 0, scheduled: 0, follow_up: 0, closed: 0, needs_person: 0,
    working: 0, median_minutes_to_call: null },
  leads_by_day: [],
  transferred_by_day: [],
  latest: [],
};

/**
 * The page Speed to Lead opens on. Every figure is read from the running system.
 *
 * Top to bottom: how a lead moves through it, the numbers so far, then the latest leads.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { data, error } = useLive(fetchStlSpotlight, 10_000, { watch: watchStlLeads });
  const [open, setOpen] = React.useState<string | null>(null);

  if (error && !data) return <EmptyState message={error} />;
  const loading = !data;
  const { flow, totals } = data ?? EMPTY;

  const nodes: FlowNode[] = [
    {
      id: 'hubspot', step: 'A lead is created', name: 'HubSpot',
      detail: flow.email_domains.length ? `Emails on ${flow.email_domains.join(', ')}` : 'No email domains set',
      logo: <HubSpotMark />, to: 'integrations',
    },
    {
      id: 'email', step: 'We email at once', name: 'Email',
      detail: 'A specialist would like a quick chat', logo: <MailRounded sx={(t) => ({ fontSize: 20, color: t.tokens.brand.primary })} />,
      to: 'live',
    },
    {
      id: 'call', step: `The AI calls ${waitWords(flow.call_after_seconds)}`, name: flow.assistant || 'AI caller',
      detail: `Have you got ${flow.chat_minutes} minutes now?`, logo: <ElevenLabsMark />, to: 'live',
    },
    {
      id: 'transfer', step: 'Yes: handed over', name: 'Transfer line',
      detail: 'The specialist presses 1 to take it', logo: <TwilioMark />, to: 'done',
    },
    {
      id: 'calendar', step: 'Not now: booked', name: 'AgencyZoom',
      detail: 'On a specialist\'s calendar, lead invited', logo: <AgencyZoomMark />, to: 'done',
    },
  ];

  const trend = (points: StlDay[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  const columns: ListColumn<StlLeadCard>[] = [
    {
      key: 'lead', header: 'Lead', grow: 1, minWidth: 150,
      render: (l) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{l.name}</Typography>,
    },
    {
      key: 'arrived', header: 'Arrived', width: 130,
      render: (l) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{fromNow(l.arrived_at)}</Typography>,
    },
    {
      key: 'status', header: 'Where it stands', grow: 1.4, minWidth: 200,
      render: (l) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <StatusChip {...liveChip(l)} />
          {l.outcome ? <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{l.outcome}</Typography> : null}
        </Stack>
      ),
    },
  ];

  const minutes = totals.median_minutes_to_call;
  return (
    <Stack spacing={2}>
      <AgentFlow nodes={nodes} loading={loading}
        subtitle="From a new lead in HubSpot to a conversation with a specialist"
        onOpen={(to) => navigate(`/agents/speed_to_lead/${to}`)} />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard loading={loading} label="New leads" value={whole(totals.leads)}
          info="HubSpot contacts on the chosen email domains. Each one was emailed and called."
          trend={trend(data?.leads_by_day ?? [], 'New leads by day, this week')} />
        <MetricCard loading={loading} label="Time to first call" value={minutes == null ? '—' : `${minutes} min`}
          info="The middle time from a lead arriving to the AI's first call." />
        <MetricCard loading={loading} label="Talked to a specialist" value={whole(totals.transferred)}
          info="Leads who had time there and then, and were put through to the specialist."
          trend={trend(data?.transferred_by_day ?? [], 'Put through by day, this week')} />
        <MetricCard loading={loading} label="Calls booked" value={whole(totals.scheduled)}
          info="Leads who picked a time. Each one is on a specialist's AgencyZoom calendar, with the lead invited." />
        <MetricCard loading={loading} label="Needed a person" value={whole(totals.needs_person)}
          info="Something stopped the agent: no phone number, or AgencyZoom would not take the booking. Each one has its reason." />
      </Box>

      <Panel title="Latest leads" subtitle="Open one to see the emails, the calls and what was booked"
        loading={loading} loadingHeight={160}>
        <ListTable columns={columns} rows={data?.latest ?? []} getRowKey={(l) => l.id} loading={loading}
          onRowClick={(l) => setOpen(l.id)}
          emptyMessage="No leads yet. A new HubSpot contact on the chosen email domains shows here within seconds." />
      </Panel>
      {open ? <LeadStoryModal leadId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
